import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { finalize, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { SharedModule } from 'src/app/shared/shared.module';
import { senhaForteValidator } from 'src/app/core/validators/senha.validators';
import { extractManagerMensagemInline, extractManagerMotivoTecnico } from 'src/app/core/utils/error.utils';
import { OtpAuthService, isRateLimitError } from 'src/app/core/services/otp-auth.service';
import { OTP_CONTA_JA_POSSUI_SENHA_STATE_KEY } from 'src/app/core/models/otp.model';
import { OtpCodigoStepController } from 'src/app/core/services/otp-codigo-step.controller';
import { OtpInputComponent } from 'src/app/shared/otp-input/otp-input.component';
import { OtpEmpresaSelecaoComponent } from 'src/app/shared/otp-empresa-selecao/otp-empresa-selecao.component';
import { FocusOnInitDirective } from 'src/app/shared/directives/focus-on-init.directive';

function senhasIguaisValidator(control: AbstractControl): ValidationErrors | null {
  const novaSenha = control.get('novaSenha')?.value;
  const confirmarSenha = control.get('confirmarSenha')?.value;
  return novaSenha && confirmarSenha && novaSenha !== confirmarSenha
    ? { senhasDivergentes: true }
    : null;
}

const FLUXO = 'primeiro-acesso' as const;

/**
 * Fluxo "Primeiro acesso" com confirmação por código OTP (4 passos no
 * stepper, ux.md §3.2 — "confirmação" aqui é o próprio passo "Termos", não
 * uma 5ª tela: o fluxo termina com toast + redirect, sem tela dedicada):
 * 1. Identificação (e-mail OU identificador — D9) [+ sub-estado: escolha de
 *    empresa]
 * 2. Código de confirmação (NOVO)
 * 3. Criar senha
 * 4. Termos
 *
 * Os passos 1 e 2 são a MESMA máquina de estado de `esqueci-senha` (spec
 * §5) — vive em `OtpCodigoStepController` pra não divergir entre os dois
 * componentes de novo (foi o que aconteceu com o ramo de rate limit do
 * `criarSenha`, que faltava aqui e existia só no `redefinirSenha`).
 *
 * Nenhuma saudação com nome antes da validação do código: `nomeUsuario` só
 * existe depois do `validar-codigo` (`primeiroNome` na resposta) — o
 * `/validar` antigo que devolvia nome no passo 1 foi removido (ADR-010 D8).
 */
@Component({
  standalone: true,
  selector: 'app-primeiro-acesso',
  templateUrl: './primeiro-acesso.component.html',
  styleUrls: ['./primeiro-acesso.component.scss'],
  providers: [MessageService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    ToastModule,
    SharedModule,
    OtpInputComponent,
    OtpEmpresaSelecaoComponent,
    FocusOnInitDirective
  ]
})
export class PrimeiroAcessoComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private http = inject(HttpClient);
  private messageService = inject(MessageService);
  private otpAuthService = inject(OtpAuthService);

  private readonly baseUrl = environment.apiBaseUrl;

  @ViewChild(OtpInputComponent) private otpInputRef?: OtpInputComponent;

  step = 1;
  loading = false;
  errorMessage = '';
  nomeUsuario = '';
  usuarioId: number | null = null;
  // BUG #66: token opaco de uso único emitido por criar-senha (prova de posse
  // do aceite LGPD). Opcional de propósito — vide fallback em aceitarTermos().
  aceiteTermosToken: string | null = null;
  showNovaSenha = false;
  showConfirmarSenha = false;
  versaoTermoVigente = '1.0';

  private resetToken: string | null = null;

  /** Passos 1 (+ escolha de empresa) e 2 (código) — máquina compartilhada com `esqueci-senha`. */
  readonly otpCode = new OtpCodigoStepController(
    FLUXO,
    this.fb,
    this.otpAuthService,
    this.messageService,
    {
      onAvancarParaCodigo: () => (this.step = 2),
      onCodigoValidado: (resetToken, primeiroNome) => {
        this.resetToken = resetToken;
        // Só existe a partir de agora (ADR-010 D8) — nunca antes do código.
        this.nomeUsuario = primeiroNome ?? '';
      },
      onAvancarParaSenha: () => (this.step = 3),
      onVoltarParaIdentificacao: () => (this.step = 1),
      onError: (mensagem) => (this.errorMessage = mensagem),
      getOtpInput: () => this.otpInputRef,
      // Conta já tem senha (spec §4.6-B/§12-C) — em vez de deixar a pessoa
      // esperando um código que nunca chega, redireciona direto pro
      // esqueci-senha já com o valor digitado (state do Router, nunca
      // query param — identificador/e-mail é dado pessoal).
      onContaJaPossuiSenha: (identificadorOuEmail) => {
        this.router.navigate(['/esqueci-senha'], {
          state: { [OTP_CONTA_JA_POSSUI_SENHA_STATE_KEY]: identificadorOuEmail }
        });
      }
    },
    'Identificador ou e-mail não encontrado.',
    this.router
  );

  // ── Passo 3 — criar senha ───────────────────────────────────────────────────
  step3Form = this.fb.nonNullable.group(
    {
      novaSenha: ['', [Validators.required, senhaForteValidator()]],
      confirmarSenha: ['', [Validators.required]]
    },
    { validators: senhasIguaisValidator }
  );

  // ── Passo 4 — termos ─────────────────────────────────────────────────────────
  step4Form = this.fb.nonNullable.group({
    termosAceitos: [false, [Validators.requiredTrue]]
  });

  ngOnInit(): void {
    this.carregarVersaoTermos();
  }

  ngOnDestroy(): void {
    this.otpCode.destroy();
  }

  /**
   * Erro sem tipo reconhecido — a caixa inline mostra no máximo o `mensagem`
   * estruturado do backend ou o fallback; o texto técnico cru (ex.: 404 de
   * rota, 500 sem envelope) vai só pro toast, nunca pra tela.
   */
  private tratarErroInesperado(err: unknown, fallbackInline: string): string {
    this.messageService.add({
      severity: 'error',
      summary: 'Erro inesperado',
      detail: extractManagerMotivoTecnico(err),
      life: 8000
    });
    return extractManagerMensagemInline(err, fallbackInline);
  }

  private carregarVersaoTermos(): void {
    this.http
      .get<{ conteudo: { versao: string } }>(`${this.baseUrl}/api/v1/auth/termos/versao-vigente`)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (res?.conteudo?.versao) {
          this.versaoTermoVigente = res.conteudo.versao;
        }
      });
  }

  // ── Passo 3 — criar senha ────────────────────────────────────────────────────

  criarSenha(): void {
    if (this.step3Form.invalid || !this.resetToken) {
      this.step3Form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const { novaSenha } = this.step3Form.getRawValue();

    this.otpAuthService
      .criarSenha(this.resetToken, novaSenha)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (resp) => {
          this.usuarioId = resp.usuarioId;
          this.aceiteTermosToken = resp.aceiteTermosToken ?? null;
          this.step = 4;
          this.errorMessage = '';
        },
        error: (err) => {
          // Paridade com `redefinirSenha` (esqueci-senha) — faltava aqui.
          if (isRateLimitError(err)) {
            this.errorMessage = 'Muitas tentativas. Aguarde alguns minutos.';
          } else {
            this.errorMessage = this.tratarErroInesperado(err, 'Não foi possível criar a senha. Tente novamente.');
          }
        }
      });
  }

  toggleNovaSenha(): void {
    this.showNovaSenha = !this.showNovaSenha;
  }

  toggleConfirmarSenha(): void {
    this.showConfirmarSenha = !this.showConfirmarSenha;
  }

  // ── Passo 4 — termos ─────────────────────────────────────────────────────────

  aceitarTermos(): void {
    if (this.step4Form.invalid || !this.usuarioId) {
      this.step4Form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.http
      .post<void>(`${this.baseUrl}/api/v1/auth/aceitar-termos`, {
        tipos: ['TERMOS_USO', 'POLITICA_PRIVACIDADE'],
        ...(this.aceiteTermosToken ? { token: this.aceiteTermosToken } : { usuarioId: this.usuarioId })
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Tudo pronto!',
            detail: 'Senha criada e termos aceitos. Redirecionando para o login...',
            life: 3000
          });
          setTimeout(() => this.router.navigate(['/login']), 2000);
        },
        error: () => {
          this.errorMessage = 'Não foi possível registrar o aceite. Tente novamente.';
        }
      });
  }

  voltarAoLogin(): void {
    this.router.navigate(['/login']);
  }
}
